import type { FC } from 'react'

import { Button } from '@skytech/ui/Button'
import { useQuery } from '@tanstack/react-query'
import FileDelimitedIcon from 'mdi-react/FileDelimitedIcon'
import MicrosoftExcelIcon from 'mdi-react/MicrosoftExcelIcon'
import TrayArrowDownIcon from 'mdi-react/TrayArrowDownIcon'
import { twJoin } from 'tailwind-merge'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { useIsThreeStepIT } from '@/hooks/access'
import queries from '@/modules/admin/queries'

import { getReportExportUsingAPI, vismaGlobalAccountingFormat } from '../exportFormats'
import { useReportsContext } from '../index.context'

import { getISODate } from './helpers'

import { exportDataToExcel } from '../../helpers'
import type { AccountingReport, ReportFilters, ReportSetupExportSettings } from '../../resources'
import adminResources from '../../resources'
import type { ExportFiltersType } from '../../resources/reports/types'

export const AccountingOptions: FC<{
  title: string
  filters: ReportFilters
  reportSetupExportSettings?: ReportSetupExportSettings
}> = ({ filters, title, reportSetupExportSettings }) => {
  const isThreeStepIT = useIsThreeStepIT()

  const { data: reports } = useQuery(queries.adminAccountingReports(filters), () =>
    adminResources.getAccountingReports(filters)
  )
  const translate = useTranslation()
  const { customerSpecificReports } = useReportsContext()

  if (!reports) throw Error('We have encountered a problem while loading reports')

  const reportsData = reports.map(report => ({
    number: report.number,
    name: report.name,
    employeeNo: report.employeeNo,
    accountingCode: report.accountingCode,
    departmentName: report.departmentName,
    departmentCode: report.departmentCode,
    dim1: report.dim1,
    dim2: report.dim2,
    dim3: report.dim3,
    dim4: report.dim4,
    fromPeriod: getISODate(report.fromPeriod),
    toPeriod: getISODate(report.toPeriod),
    project: report.project,
    netAmount: report.netAmount ?? '',
    vat: report.vatAmount ?? '',
    totalAmount: report.totalAmount ?? '',
    organisationNumber: report.orgNo,
    companyName: report.companyName,
    invoiceNumber: report.invoiceNo,
  }))

  const columnFormats = {
    fromPeriod: 'date',
    toPeriod: 'date',
    netAmount: 'amount',
    vat: 'amount',
    totalAmount: 'amount',
  }

  const reportsWithVendorNetAmountByInvoice = reports.reduce<Record<string, AccountingReport[]>>(
    (acc, report) => {
      if (!report.vendorNetAmount) return acc
      if (!acc[report.invoiceNo]) {
        acc[report.invoiceNo] = [report]
        return acc
      }
      acc[report.invoiceNo].push(report)
      return acc
    },
    {}
  )

  const roundingDiffRecords = Object.values(reportsWithVendorNetAmountByInvoice).map(reports => {
    // All reports of the same invoice get the same values for the following attributes
    const { vendorNetAmount, invoiceNo, vendorGrossAmount, vendorVatAmount, orgNo, companyName } =
      reports[0]
    const invoiceAmounts = reports.reduce(
      (acc, report) => {
        acc.netAmount += report.netAmount
        acc.vatAmount += report.vatAmount
        acc.totalAmount += report.totalAmount
        return acc
      },
      { netAmount: 0, vatAmount: 0, totalAmount: 0 }
    )

    return {
      number: '',
      name: 'rounding diff',
      employeeNo: '',
      accountingCode: '',
      departmentName: '',
      departmentCode: '',
      dim1: '',
      dim2: '',
      dim3: '',
      dim4: '',
      fromPeriod: '',
      toPeriod: '',
      project: '',
      netAmount: vendorNetAmount - invoiceAmounts.netAmount,
      vat: vendorVatAmount - invoiceAmounts.vatAmount,
      totalAmount: vendorGrossAmount - invoiceAmounts.totalAmount,
      organisationNumber: orgNo,
      companyName: companyName,
      invoiceNumber: invoiceNo,
    }
  })

  reportsData.push(...roundingDiffRecords)

  const companyNames = reports.map(report => report.companyName)
  let companyName = ''
  if (companyNames.length > 0) {
    companyName = companyNames[0]
  }

  const handleReportExport = (formatName: ExportFiltersType['formatName']) => {
    getReportExportUsingAPI({
      reportName: 'accounting',
      formatName,
      isGlobal: filters.isGlobal,
      fromDate: filters.fromDate,
      toDate: filters.toDate,
      customerId: filters.selectedCustomer,
    })
  }

  const customerSpecificReport = customerSpecificReports.find(
    element => element.report === filters.selectedReportType
  )
  return (
    <div className="grid grid-cols-2">
      <Button
        Icon={<MicrosoftExcelIcon className={twJoin(!isThreeStepIT && 'text-emerald-600')} />}
        className="mb-4 ml-0"
        onClick={() =>
          exportDataToExcel(reportsData, title, {
            translate,
            columnFormats,
            fromDate: filters.fromDate,
            toDate: filters.toDate,
          })
        }
      >
        <Translation id="Excel" />
      </Button>
      <Button
        Icon={<FileDelimitedIcon className={twJoin(!isThreeStepIT && 'text-emerald-600')} />}
        className="mb-4 ml-0"
        onClick={() =>
          exportDataToExcel(reports, title, {
            extension: 'csv',
            translate,
            columnFormats,
            fromDate: filters.fromDate,
            toDate: filters.toDate,
          })
        }
      >
        <Translation id="CSV" />
      </Button>

      {reportSetupExportSettings && (
        <>
          <Button
            Icon={<TrayArrowDownIcon />}
            className="mb-4 ml-0"
            onClick={() => vismaGlobalAccountingFormat(filters, reports)}
          >
            <Translation id="Visma" />
          </Button>

          <Button Icon={<TrayArrowDownIcon />} onClick={() => handleReportExport('sap_v2')}>
            <Translation id="SAP v2" />
          </Button>

          {customerSpecificReport && (
            <Button
              Icon={<TrayArrowDownIcon />}
              onClick={() => handleReportExport('customer_specific')}
            >
              {customerSpecificReport.friendlyName ?? companyName}
            </Button>
          )}
        </>
      )}
    </div>
  )
}
